import { Modal } from 'antd';
import styled from 'styled-components';

import { Checkbox } from '@components/Checkbox';
import { Input } from '@components/Input';

export const StyledModal = styled(Modal)`
  &.ant-modal {
    width: 520px !important;
    max-width: calc(100dvw - 32px) !important;
    height: 614px !important;

    @media (max-width: 768px) {
      width: 100dvw !important;
      max-width: none !important;
      height: 100dvh !important;
    }
    .ant-modal-content {
      padding: 0;
      border-radius: 4px;
      color: ${({ theme }) => theme.palette.text[0]};

      @media (max-width: 768px) {
        border-radius: 0;
      }

      button.ant-modal-close {
        display: none;
      }

      .ant-modal-body {
        font-size: 14px;
        line-height: 20px;

        @media (max-width: 768px) {
          font-size: 16px;
          line-height: 24px;
        }
      }
    }
  }
`;

export const StyledHeader = styled.div`
  padding: 16px 20px 15px 20px;
  font-size: 17px;
  line-height: 21px;
  color: #000000;
  font-weight: 600;
  border-bottom: 1px solid ${({ theme }) => theme.palette.stroke[0]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;

  @media (max-width: 768px) {
    flex-direction: row-reverse;
    font-size: 16px;
    line-height: 20px;
    padding: 8px 8px 7px 8px;
  }

  .close-button {
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      path {
        stroke: ${({ theme }) => theme.palette.stroke[3]};
      }
    }
  }

  @media (max-width: 768px) {
    .close-button-container {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      width: 93px;

      .close-button {
        width: 40px;
        height: 40px;

        svg {
          width: 24px;
          height: 24px;
        }
      }
    }
  }

  .reset-all-button {
    display: none;

    &:disabled {
      opacity: 0.4;
    }

    @media (max-width: 768px) {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      line-height: 20px;
      color: ${({ theme }) => theme.palette.primary[0]};
      padding: 10px 16px;
      width: 93px;
    }
  }
`;

export const StyledBody = styled.div`
  padding: 24px;
  height: 497px;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 20px;
    height: calc(100dvh - 204px);
  }
`;

type StyledChipsContainerProps = {
  applyMobileWidthReduction?: boolean;
};
export const StyledChipsContainer = styled.div<StyledChipsContainerProps>`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 0;

  .chip-select-option {
    @media (max-width: 768px) {
      width: ${({ applyMobileWidthReduction }) =>
        applyMobileWidthReduction ? 'calc(50% - 4px)' : 'auto'};
    }
  }
`;

export const StyledCheckboxGroup = styled(Checkbox.Group)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
  .ant-checkbox-wrapper {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding-left: 2.3px;

    @media (max-width: 768px) {
      padding-left: 3.3px;
    }

    span:nth-child(2) {
      padding: 0;
      font-size: 14px;
      line-height: 20px;

      @media (max-width: 768px) {
        font-size: 16px;
        line-height: 24px;
      }
    }

    .ant-checkbox {
      top: 0;
      .ant-checkbox-inner {
        scale: 1.125;
        border-radius: 2px;
        border-width: 1.3px;

        @media (max-width: 768px) {
          scale: 1.25;
        }
      }
    }
    ::after {
      display: none;
    }
  }
`;

export const StyledFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.palette.stroke[0]};
  padding: 15px 20px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  @media (max-width: 768px) {
    padding: 19px 20px 20px;
  }

  .reset-all-button {
    font-size: 12px;
    line-height: 15px;
    color: ${({ theme }) => theme.palette.primary[0]};
    font-weight: 600;

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    @media (max-width: 768px) {
      display: none;
    }
  }

  .buttons-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    @media (max-width: 768px) {
      flex-direction: column;
      width: 100%;
      align-items: stretch;
      justify-content: flex-start;
      gap: 12px;
    }

    .footer-button {
      border-radius: 4px;
      height: 32px;

      padding: 5px 11px;
      font-size: 14px;
      line-height: 20px;
      font-weight: 600;
      border-width: 1px;

      @media (max-width: 768px) {
        padding: 11px 0;
        height: 48px;
        font-size: 16px;
      }

      &.ant-btn-default {
        border: 1px solid ${({ theme }) => theme.palette.stroke[0]};
        &:not(:hover) {
          background: #ffffff;
        }
      }

      &.save-button {
        @media (max-width: 768px) {
          width: 100%;
        }
      }
    }
  }
`;

export const StyledInput = styled(Input)`
  &.ant-input,
  &.ant-input-affix-wrapper {
    height: 32px;
    padding: 7px 11px;
    font-size: 12px;
    line-height: 16px;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.palette.border[5]};

    @media (max-width: 768px) {
      height: 40px;
      padding: 7px 15px;
      font-size: 16px;
      line-height: 24px;
    }
  }
`;
